import { Injectable, OnDestroy } from "@angular/core";
import { AccountAppCommand, AccountAppValue } from "@fabric-types/app-account";
import { CustomerSupportService } from "@obg/core.customer-support";
import { KycConfig } from "@obg/ngx.common";
import {
	AccountStatus,
	CORE_ROUTE,
	CurrentCompliance,
	Jurisdiction,
	KycState,
	AccountInformation,
} from "@obg/ngx.core";
import { CacheFactory, CacheStore, fromDaysToMilliseconds, ignoreNil, StorageMode } from "@obg/ngx.global";
import { DocumentRef } from "@obg/ngx.global.evo";
import { Observable, Subscription } from "rxjs";
import { map, tap } from "rxjs/operators";

import {
	DocumentExpiredDaysLeft,
	KYC_NOTIFICATION_ACCOUNT_WILL_BE_LIMITED,
	KYC_NOTIFICATION_DOCUMENTS_EXPIRED,
	KYC_NOTIFICATION_VERIFY_NOW,
	MINOR_ERROR,
	MINOR_ERROR_MAX_UPDATE_DAYS,
	SUCCESSFUL_FULLY_VERIFIED_KYC_LINK_TEXT,
	SUCCESSFUL_FULLY_VERIFIED_KYC_MESSAGE,
	SUCCESSFUL_NOTIFICATION_SHOWN_KEY,
} from "./kyc-notification.const";
import { DefineNotificationParams, KycNotification, KycNotificationType } from "./kyc-notification.model";

@Injectable({
	providedIn: "root",
})
export class KycNotificationService implements OnDestroy {
	private cache: CacheStore;

	private depositThresholdReached = false;
	private depositThresholdEnabled = false;

	private data$$: Subscription | undefined;
	private initDialogSubscription$$: Subscription | undefined;

	constructor(
		cacheFactory: CacheFactory,
		private kycConfig: KycConfig,
		private customerSupportService: CustomerSupportService,
		private documentRef: DocumentRef
	) {
		this.cache = cacheFactory.get(SUCCESSFUL_NOTIFICATION_SHOWN_KEY, {
			storageMode: StorageMode.LocalStorage,
			maxAge: fromDaysToMilliseconds(1),
		});

		if (this.documentRef.obgEventBus) {
			this.data$$ = new Observable(this.documentRef.obgEventBus.retrieve(AccountAppValue.FetchThresholdStatus))
				.pipe(
					ignoreNil(),
					tap((hasReached) => {
						this.depositThresholdReached = hasReached;
					})
				)
				.subscribe();

			this.documentRef.obgEventBus?.dispatch(AccountAppCommand.GetDepositThresholdEnabled, {
				onSuccess: (isEnabled: boolean) => {
					this.depositThresholdEnabled = isEnabled;
				},
			});
		}
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
		this.initDialogSubscription$$?.unsubscribe();
	}

	getKycNotificationType(compliance: CurrentCompliance): KycNotification | undefined {
		const { kycState, kycSubStatus, type, accountInformation } = compliance;

		if (this.shouldSkipDueToDepositThreshold()) {
			return undefined;
		}

		if (this.shouldHandleFullyVerificationBar(kycState)) {
			return this.handleFullyVerificationBar(compliance);
		}

		if (this.shouldGenerateAgeVerificationNotification(accountInformation, type, kycState)) {
			return this.buildKycNotification({
				message: `kyc-verify-popup.verify-completion`,
				linkText: "kyc-verify-popup.verify",
				destination: CORE_ROUTE.verification.root,
				type: KycNotificationType.Warning,
			});
		}

		const isAccountTemporary = accountInformation?.accountStatus === AccountStatus.Temporary;
		const isAccountTemporaryLimited = accountInformation?.accountStatus === AccountStatus.TemporaryLimited;
		const isAccountTemporaryPreClosure = accountInformation?.accountStatus === AccountStatus.TemporaryPreClosure;
		const isAccountTemporaryExpired = accountInformation?.accountStatus === AccountStatus.TemporaryExpired;

		const isGenericVerification =
			this.kycConfig.getGenericVerificationEnabled() &&
			(isAccountTemporary || isAccountTemporaryLimited || isAccountTemporaryPreClosure);
		const isVerificationThroughDeposit =
			this.kycConfig.getVerificationThroughDepositEnabled() &&
			[KycState.Requested, KycState.None].includes(kycState!) &&
			isAccountTemporary;

		const isOnlyBankKycRequested =
			kycSubStatus?.name === KycState.Accepted &&
			kycSubStatus?.age === KycState.Accepted &&
			kycSubStatus?.address === KycState.Accepted &&
			kycSubStatus?.bank === KycState.Requested;

		const isVerificationExpired =
			isOnlyBankKycRequested && Date.now() > new Date(accountInformation!.accountExpiry).getTime();

		if (isVerificationThroughDeposit && isVerificationExpired) {
			return this.buildKycNotification({
				message: `kyc-verify-popup.temp-account-expired-message`,
				linkText: "kyc-verify-popup.verify",
				destination: CORE_ROUTE.verification.root,
				type: KycNotificationType.Warning,
			});
		}

		const daysLeft = this.tempAccountDayCalc(accountInformation?.accountExpiry);

		if (isGenericVerification || isVerificationThroughDeposit) {
			const tempAccountMessage =
				daysLeft > 0
					? "kyc-verify-popup.temp-account-message"
					: "kyc-verify-popup.temp-account-restricted-message";

			return daysLeft > 0
				? this.buildKycNotification({
						message: tempAccountMessage,
						linkText: "kyc-verify-popup.temp-account-verify",
						destination: CORE_ROUTE.verification.kycMethodSelection,
						type: KycNotificationType.Warning,
						interpolateParams: { avaliableDaysLeft: daysLeft },
					})
				: this.buildKycNotification({
						message: tempAccountMessage,
						linkText: "kyc-verify-popup.support",
						destination: this.customerSupportService.pageURL,
						type: KycNotificationType.Warning,
						interpolateParams: undefined,
					});
		}

		if (this.shouldGenerateUnverifiedNotification(kycState) && !isAccountTemporaryExpired) {
			return this.buildKycNotification({
				message: "kyc-verify-popup.unverified",
				linkText: "kyc-verify-popup.verify",
				destination: CORE_ROUTE.verification.root,
				type: KycNotificationType.Warning,
			});
		}

		if (this.hasMinorError(compliance)) {
			return this.buildKycNotification({
				message: "kyc-verify-popup.minor-error",
				linkText: "kyc-verify-popup.update-now",
				destination: CORE_ROUTE.customerManagement.myProfile,
				type: KycNotificationType.Warning,
				interpolateParams: {
					daysLeft: this.getDaysLeft(
						MINOR_ERROR_MAX_UPDATE_DAYS,
						compliance.accountInformation?.accountCreationDate
					),
				},
			});
		}

		if (
			accountInformation?.accountStatus === AccountStatus.TemporaryAwaitingVerification &&
			compliance.jurisdiction
		) {
			return this.buildTemporaryAwaitingVerificationKycNotification(daysLeft, compliance.jurisdiction);
		}

		if (this.shouldGenerateDocumentExpirationNotification(isAccountTemporaryExpired)) {
			return this.generateDocumentExpirationNotification(daysLeft);
		}

		return undefined;
	}

	notificationIsExpired(notificationData: KycNotification | undefined): Observable<boolean> {
		return this.cache.getItemAsObservable<number>(SUCCESSFUL_NOTIFICATION_SHOWN_KEY).pipe(
			map((cache) => {
				let isExpired = false;
				if (cache && notificationData?.message === SUCCESSFUL_FULLY_VERIFIED_KYC_MESSAGE) {
					const now = Date.now();
					const expirationTime = new Date(cache).getTime();
					if (expirationTime <= now) {
						isExpired = true;
					}
				}
				return isExpired;
			})
		);
	}

	private tempAccountDayCalc(dateString?: string) {
		if (!dateString) {
			return 0;
		}
		if (dateString.slice(0, 10).split("-")[2].length === 4) {
			dateString = `${dateString.slice(0, 10).split("-").reverse().join("-")}T${dateString.split("T")[1]}`;
		}
		const date = new Date(dateString);
		const difference = date.getTime() - new Date().getTime();
		return Math.ceil(difference / (1000 * 3600 * 24));
	}

	private getDaysLeft(maxDays: number, startDateString?: string) {
		if (!startDateString) {
			return 0;
		}

		const startDate = new Date(startDateString);

		if (isNaN(startDate.getTime())) {
			return 0;
		}

		const difference = new Date().getTime() - startDate.getTime();
		const differenceDays = Math.ceil(maxDays - difference / (1000 * 3600 * 24));

		if (differenceDays < 0) {
			return 0;
		}

		return differenceDays;
	}

	private cacheFullyVerificationBar() {
		const durationToExpire = this.kycConfig.getFullyVerificationBarTimeout();
		const timeToExpire = Date.now() + durationToExpire;

		this.cache.getItem(SUCCESSFUL_NOTIFICATION_SHOWN_KEY).then((cache) => {
			if (!cache) {
				this.cache.setItem(SUCCESSFUL_NOTIFICATION_SHOWN_KEY, timeToExpire);
			}
		});
	}

	private buildTemporaryAwaitingVerificationKycNotification(
		daysLeftUntilSuspension: number,
		jurisdiction: Jurisdiction
	): KycNotification {
		const isSuspendedMessage = daysLeftUntilSuspension <= 0;

		if (isSuspendedMessage) {
			const kycNotificationType = this.determineSuspendedKycNotificationType(jurisdiction);

			return this.buildKycNotification({
				message: "kyc-verify-popup.temp-account-suspended-message",
				linkText: "kyc-verify-popup.verify-now",
				destination: CORE_ROUTE.verification.root,
				type: kycNotificationType,
				interpolateParams:
					daysLeftUntilSuspension > 0 ? { avaliableDaysLeft: daysLeftUntilSuspension } : undefined,
			});
		}

		return this.buildKycNotification({
			message: "kyc-verify-popup.temp-account-40-verify-message",
			linkText: "kyc-verify-popup.verify-now",
			destination: CORE_ROUTE.verification.root,
			type: KycNotificationType.Warning,
			interpolateParams: daysLeftUntilSuspension > 0 ? { avaliableDaysLeft: daysLeftUntilSuspension } : undefined,
		});
	}

	private buildKycNotification({
		message,
		linkText,
		destination,
		interpolateParams,
		type = KycNotificationType.Default,
	}: DefineNotificationParams): KycNotification {
		return {
			message,
			linkText,
			destination,
			type,
			interpolateParams,
		};
	}

	private determineSuspendedKycNotificationType(jurisdiction: Jurisdiction): KycNotificationType {
		switch (jurisdiction) {
			case Jurisdiction.COGA:
				return KycNotificationType.Danger;
			default:
				return KycNotificationType.Warning;
		}
	}

	private shouldGenerateDocumentExpirationNotification(isAccountTemporaryExpired: boolean): boolean {
		const isDocumentExpiredEnabled = this.kycConfig.getDocumentExpiredEnabled();

		return isAccountTemporaryExpired && isDocumentExpiredEnabled;
	}

	private shouldSkipDueToDepositThreshold(): boolean {
		return this.depositThresholdEnabled && !this.depositThresholdReached;
	}

	private shouldHandleFullyVerificationBar(kycState: KycState | undefined): boolean {
		return this.kycConfig.getIsFullyVerificationBarEnabled() && kycState === KycState.Accepted;
	}

	private handleFullyVerificationBar(compliance: CurrentCompliance): KycNotification | undefined {
		const kycLastChangeTimeStamp = Date.parse(compliance.kycLastChangeTimeStamp ?? "");
		const lastLogins = compliance.lastLogins ?? [];
		const currentLogin = Date.parse(lastLogins[0]?.timestamp ?? "");
		const previousLogin = Date.parse(lastLogins[1]?.timestamp ?? "");
		const isFirstLogin = lastLogins.length === 1;
		const duringLoggedIn = kycLastChangeTimeStamp >= currentLogin;
		const duringLoggedOut = kycLastChangeTimeStamp >= previousLogin && kycLastChangeTimeStamp < currentLogin;

		if (isFirstLogin || duringLoggedIn || duringLoggedOut) {
			this.cacheFullyVerificationBar();

			return this.buildKycNotification({
				message: SUCCESSFUL_FULLY_VERIFIED_KYC_MESSAGE,
				linkText: SUCCESSFUL_FULLY_VERIFIED_KYC_LINK_TEXT,
				destination: CORE_ROUTE.payment.deposit,
				type: KycNotificationType.Success,
			});
		}

		return undefined;
	}

	private shouldGenerateAgeVerificationNotification(
		accountInformation: AccountInformation | undefined,
		type: string,
		kycState: KycState | undefined
	): boolean {
		return (
			((this.kycConfig.getAgeVerificationEnabled() &&
				accountInformation?.accountStatus === AccountStatus.TemporaryAwaitingVerification) ||
				type === "Dutch") &&
			[KycState.Requested, KycState.None].includes(kycState!)
		);
	}

	private shouldGenerateUnverifiedNotification(kycState: KycState | undefined): boolean {
		return (
			this.kycConfig.getGenericVerificationEnabled() && [KycState.Requested, KycState.None].includes(kycState!)
		);
	}

	private hasMinorError(compliance: CurrentCompliance): boolean {
		return compliance.playerState?.status === MINOR_ERROR;
	}

	generateDocumentExpirationNotification(daysLeft: number): KycNotification | undefined {
		if (daysLeft === undefined) {
			return undefined;
		}

		const message =
			daysLeft <= DocumentExpiredDaysLeft.DOCUMENTS_EXPIRED
				? KYC_NOTIFICATION_DOCUMENTS_EXPIRED
				: KYC_NOTIFICATION_ACCOUNT_WILL_BE_LIMITED;

		return this.buildKycNotification({
			message,
			linkText: KYC_NOTIFICATION_VERIFY_NOW,
			destination: CORE_ROUTE.verification.kycMethodSelection,
			type: KycNotificationType.Warning,
			interpolateParams: { avaliableDaysLeft: daysLeft },
		});
	}
}
